import { memo, useCallback, useMemo } from 'react';
import { Stack, Text } from '@mantine/core';
import { IconNote, IconHistory } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { LoadingState } from '@/components/common/loading';
import { FilePreviewModal } from '@/components/common/FilePreviewModal';
import type { CollapsibleTabItem } from '@/components/common/CollapsibleTabs';
import { DetailPanelLayout } from '@/components/common/DetailPanelLayout';
import { NavigationTabs } from '@/components/common/NavigationTabs';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import {
  useDetailOverlayFullscreen,
  useRegisterDetailSlots,
} from '@/components/overlays/UnifiedDetailOverlay';
import { useGeoMarkupStore } from '@/store';
import { selectDetailedNote } from '@/store/selectors/noteSelectors';
import { useFilePreview } from '@/hooks/useFilePreview';
import { useDeviceLayout } from '@/hooks/useDeviceLayout';
import {
  downloadAndSaveAttachment,
  noteAttachmentsToPreviewable,
} from '@/utils/attachmentDownload';
import {
  DetailHeader,
  NoteAttachmentList,
  NoteDetailContentView,
  NoteDetailSidebarAside,
} from './components';
import { useNoteDetailState } from './hooks/useNoteDetailState';
import type { NoteDetailTabType } from './hooks/useNoteDetailState';
import { useNoteDetailPanelNavigation } from './hooks/useNoteDetailPanelNavigation';
import { useNoteDetailPanelActions } from './hooks/useNoteDetailPanelActions';
import styles from './NoteDetailPanel.module.css';

const EMPTY_ATTACHMENTS: never[] = [];

/**
 * Detail content for the Note artifact, rendered inside
 * `UnifiedDetailOverlay`. Publishes header and toolbar callbacks to the
 * shell via `useRegisterDetailSlots`; reads fullscreen state from the
 * shared `DetailOverlayContext` so sidebar layout stays in sync with the
 * shell's expand/collapse control.
 */
export const NoteDetailContent = memo(function NoteDetailContent() {
  const { t } = useTranslation();

  const selectedNote = useGeoMarkupStore(selectDetailedNote);
  const isSyncing = useGeoMarkupStore((state) => state.notes.isSyncing);
  const currentUserId = useGeoMarkupStore(
    (state) => state.auth.user?.id ?? null
  );
  const previewMode = useGeoMarkupStore(
    (state) => state.ui.slidePanel.previewMode
  );
  const setPreviewMode = useGeoMarkupStore((state) => state.ui.setPreviewMode);

  const {
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
    handleTaskNavigate,
    openArtifact,
    closeOverlay,
  } = useNoteDetailPanelNavigation(selectedNote);

  const {
    deleteConfirmOpen,
    handleEdit,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
  } = useNoteDetailPanelActions(selectedNote, { openArtifact, closeOverlay });

  const previewableAttachments = useMemo(
    () =>
      noteAttachmentsToPreviewable(
        selectedNote?.attachments ?? EMPTY_ATTACHMENTS
      ),
    [selectedNote?.attachments]
  );

  const preview = useFilePreview(previewableAttachments);
  const { openPreview } = preview;

  const handleAttachmentDownload = useCallback(
    async (attachment: {
      id: string;
      fileName: string;
      contentType: string;
    }) => {
      try {
        await downloadAndSaveAttachment({
          id: attachment.id,
          fileName: attachment.fileName,
          contentType: attachment.contentType,
          source: 'note',
        });
      } catch (error) {
        console.error('Failed to download attachment:', error);
      }
    },
    []
  );

  const { isFullscreenMode } = useDetailOverlayFullscreen();
  const { isMobile } = useDeviceLayout();

  const { activeTab, setActiveTab } = useNoteDetailState(selectedNote?.id);

  const firstLinkedTask = selectedNote?.linkedTasks[0];

  const handleSectionTabChange = useCallback(
    (tabId: string) => {
      setActiveTab(tabId as NoteDetailTabType);
    },
    [setActiveTab]
  );

  const sectionTabs = useMemo<CollapsibleTabItem[]>(
    () => [
      {
        id: 'content',
        label: t('notes.detail.tabs.content'),
        icon: IconNote,
      },
      {
        id: 'history',
        label: t('notes.detail.tabs.history'),
        icon: IconHistory,
      },
    ],
    [t]
  );

  const isAuthor = selectedNote
    ? currentUserId === selectedNote.createdBy
    : false;
  const hasLocation =
    selectedNote?.latitude != null && selectedNote?.longitude != null;
  const showSidebar = isFullscreenMode && !isMobile;
  const showMetadataSidebar =
    showSidebar && activeTab === 'content' && hasLocation;

  const attachmentsContent = useMemo(() => {
    if (!selectedNote || selectedNote.attachments.length === 0) return null;
    return (
      <NoteAttachmentList
        attachments={selectedNote.attachments}
        onPreview={openPreview}
        onDownload={handleAttachmentDownload}
      />
    );
  }, [selectedNote, openPreview, handleAttachmentDownload]);

  const headerContent = useMemo(() => {
    if (!selectedNote) return undefined;
    if (showSidebar) {
      return tabs.length > 1 ? (
        <NavigationTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabClick={handleTabClick}
          onTabClose={handleTabClose}
        />
      ) : undefined;
    }
    return (
      <Stack gap="xs" className={styles.headerStack}>
        <NavigationTabs
          tabs={tabs}
          activeTabId={activeTabId}
          onTabClick={handleTabClick}
          onTabClose={handleTabClose}
        />
        <DetailHeader note={selectedNote} />
      </Stack>
    );
  }, [
    selectedNote,
    showSidebar,
    tabs,
    activeTabId,
    handleTabClick,
    handleTabClose,
  ]);

  useRegisterDetailSlots({
    headerContent,
    onEdit: isAuthor ? handleEdit : undefined,
    onDelete: isAuthor ? handleDeleteClick : undefined,
    fullscreenWideContent: showSidebar,
    defaultFullscreen: false,
    previewMode: previewMode && isMobile,
    onPreviewModeChange: setPreviewMode,
  });

  if (!selectedNote && !isSyncing) {
    return null;
  }

  return (
    <>
      {selectedNote ? (
        <DetailPanelLayout
          mode={showSidebar ? 'sidebar' : 'tabs'}
          sectionItems={sectionTabs}
          activeSection={activeTab}
          onSectionChange={handleSectionTabChange}
          sidebarHeader={<DetailHeader note={selectedNote} />}
          sidebarHeaderBordered={false}
          widePreview
          aside={
            showMetadataSidebar ? (
              <NoteDetailSidebarAside
                note={selectedNote}
                firstLinkedTask={firstLinkedTask}
                onTaskNavigate={handleTaskNavigate}
                attachmentsContent={attachmentsContent}
              />
            ) : undefined
          }
        >
          <NoteDetailContentView
            note={selectedNote}
            activeTab={activeTab}
            showMetadataSidebar={showMetadataSidebar}
            firstLinkedTask={firstLinkedTask}
            onTaskNavigate={handleTaskNavigate}
            attachmentsContent={attachmentsContent}
          />
        </DetailPanelLayout>
      ) : isSyncing ? (
        <div className={styles.loadingContainer}>
          <LoadingState centered />
        </div>
      ) : (
        <div className={styles.loadingContainer}>
          <Text c="dimmed">{t('notes.detail.loadError')}</Text>
        </div>
      )}

      {selectedNote && (
        <DeleteConfirmationDialog
          isOpen={deleteConfirmOpen}
          itemName={selectedNote.title}
          itemType="note"
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
        />
      )}

      <FilePreviewModal {...preview.modalProps} />
    </>
  );
});
